import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { FilterField } from "@/components/app/patterns/filter-field";
import { FilterPopover } from "@/components/app/patterns/filter-popover";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Switch } from "@/components/ui/switch";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  optionalOneOf,
  optionalString,
  KEEP_PLACE,
  searchPatch,
  optionalSearch,
} from "@/lib/search";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { CategoryPanel } from "./-components/category-panel";
import { TemplateTable } from "./-components/template-table";
import type { TemplateRow } from "./-components/types";
import { SearchField } from "@/components/app/patterns/search-field";

const STATUSES = ["draft", "active", "archived"] as const;

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/document-templates/",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; category?: string; status?: (typeof STATUSES)[number]; superseded?: true } => ({
    q: optionalSearch(search.q),
    category: optionalString(search.category) || undefined,
    status: optionalOneOf(STATUSES, search.status),
    superseded: search.superseded === true ? true : undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "document-templates"),
  component: DocumentTemplatesPage,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The forms a building sends out to be signed, and how they are filed. */
function DocumentTemplatesPage() {
  const { facilityId } = useFacility();
  const canWrite = useCan("settings:manage");
  const canCurate = useCan("platform:manage");
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);

  const {
    q,
    category: categoryParam,
    status: statusParam,
    superseded: includeSuperseded = false,
  } = Route.useSearch();
  const category = categoryParam ?? "all";
  const status = statusParam ?? "all";
  const refine = (next: Partial<ReturnType<typeof Route.useSearch>>) => patch({ ...next });
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => refine({ q: next }));
  const setCategory = (next: string) => refine({ category: next === "all" ? undefined : next });
  const setStatus = (next: string) => refine({ status: optionalOneOf(STATUSES, next) });
  const setIncludeSuperseded = (next: boolean) => refine({ superseded: next ? true : undefined });
  const [archiving, setArchiving] = useState<TemplateRow | null>(null);
  const [forking, setForking] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  const categories = useQuery(
    orpc.administration.documentTemplates.categories.list.queryOptions({
      input: { includeInactive: true },
    }),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
  );
  const templates = useQuery(
    orpc.administration.documentTemplates.list.queryOptions({
      input: {
        facilityId,
        categoryId: category === "all" ? undefined : category,
        status: status === "all" ? undefined : (status as (typeof STATUSES)[number]),
        includeSuperseded,
      },
    }),
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.documentTemplates.key() });

  const fork = useMutation(
    orpc.administration.documentTemplates.fork.mutationOptions({
      onSuccess: async (created) => {
        toast.success(`Draft v${created.version} created; the published version is unchanged.`);
        await invalidate();
      },
      onError: fail,
      onSettled: () => setForking(null),
    }),
  );
  const duplicate = useMutation(
    orpc.administration.documentTemplates.duplicate.mutationOptions({
      onSuccess: async (created) => {
        toast.success(`“${created.name}” started as a draft with every field box carried over.`);
        await invalidate();
      },
      onError: fail,
      onSettled: () => setDuplicating(null),
    }),
  );
  const remove = useMutation(
    orpc.administration.documentTemplates.remove.mutationOptions({
      onSuccess: async (result) => {
        toast.success(result.archived ? "Template archived" : "Template deleted");
        setArchiving(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const query = search.trim().toLowerCase();
  const rows = (templates.data ?? []).filter((template) => {
    if (!query) return true;
    return (
      template.name.toLowerCase().includes(query) ||
      (template.description ?? "").toLowerCase().includes(query)
    );
  });

  const filtered = Boolean(query) || category !== "all" || status !== "all";
  const clearFilters = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
  };

  const categoryName = (id: string | null) =>
    categories.data?.find((row) => row.id === id)?.name ?? "Uncategorized";
  const facilityName = (id: string | null) =>
    id ? (facilities.data?.find((row) => row.id === id)?.name ?? "One building") : "Every building";

  return (
    <>
      <PageHeader
        title="Document templates"
        meta="The forms you send out to be signed: the PDF, who signs it, and where. A published template is locked, so a change goes into a new version."
      />

      <ListToolbar
        search={
          <SearchField
            label="Search templates"
            value={search}
            placeholder="Name or description"
            className="sm:w-full"
            onChange={setSearch}
          />
        }
        chips={
          <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
            <legend className="sr-only">Status</legend>
            {(["all", ...STATUSES] as const).map((value) => (
              <FilterChip key={value} selected={status === value} onClick={() => setStatus(value)}>
                {value === "all" ? "All" : value.charAt(0).toUpperCase() + value.slice(1)}
              </FilterChip>
            ))}
          </fieldset>
        }
        trailing={
          <FilterPopover
            active={(category === "all" ? 0 : 1) + (includeSuperseded ? 1 : 0)}
            onClear={() => {
              refine({ category: undefined, superseded: undefined });
            }}
          >
            <FilterField label="Category">
              <OptionSelect
                className="w-full"
                aria-label="Category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                <SelectOption value="all">All categories</SelectOption>
                {(categories.data ?? []).map((row) => (
                  <SelectOption key={row.id} value={row.id}>
                    {row.name}
                  </SelectOption>
                ))}
              </OptionSelect>
            </FilterField>
            <label htmlFor="template-superseded" className="flex items-center gap-2 text-sm">
              <Switch
                id="template-superseded"
                checked={includeSuperseded}
                onCheckedChange={setIncludeSuperseded}
              />
              Show older versions
            </label>
          </FilterPopover>
        }
      />

      <RecordCard
        title="Templates"
        count={templates.data ? rows.length : undefined}
        canWrite={canWrite}
        addLabel="Add template"
        onAdd={() => void navigate({ to: "/settings/document-templates/new" })}
      >
        {templates.isPending ? (
          <RowsSkeleton />
        ) : templates.isError ? (
          <ReadFailed
            message="The templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : rows.length === 0 ? (
          filtered ? (
            <p className="text-muted-foreground text-sm">
              No template matches these filters.{" "}
              <Button
                type="button"
                variant="link"
                className="h-auto min-h-0 p-0"
                onClick={clearFilters}
              >
                Clear filters
              </Button>
            </p>
          ) : (
            <EmptyRows text="No templates yet. Add one to send a form out for signature, like an admission agreement." />
          )
        ) : (
          <TemplateTable
            rows={rows}
            categoryName={categoryName}
            facilityName={facilityName}
            canWrite={canWrite}
            busyId={remove.isPending ? (archiving?.id ?? null) : (forking ?? duplicating)}
            onFork={(template) => {
              setForking(template.id);
              fork.mutate({ templateId: template.id });
            }}
            onDuplicate={(template) => {
              setDuplicating(template.id);
              duplicate.mutate({ templateId: template.id });
            }}
            onArchive={setArchiving}
          />
        )}
      </RecordCard>

      {/* One list for every organization; a customer only files templates under it. */}
      {canCurate ? <CategoryPanel categories={categories} /> : null}

      {archiving ? (
        <RemoveConfirm
          title={
            archiving.status === "draft"
              ? `Delete “${archiving.name}”?`
              : `Archive “${archiving.name}”?`
          }
          description={
            archiving.status === "draft"
              ? "This draft is deleted for good, unless something has already been sent with it, in which case it is archived instead."
              : "It stops being offered for new documents. Anything already signed with it keeps printing exactly as it was signed."
          }
          confirmLabel={archiving.status === "draft" ? "Delete draft" : "Archive template"}
          destructive={archiving.status === "draft"}
          onConfirm={() => remove.mutate({ templateId: archiving.id })}
          onClose={() => setArchiving(null)}
        />
      ) : null}
    </>
  );
}
